import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faFileAlt,
  faGlobe,
  faDatabase,
  faCogs,
  faFileCode,
  faExclamationTriangle,
  faInfoCircle,
  faTimes,
  faCheck,
  faRobot,
  faCode,
  faRefresh,
  faLink,
  faBrain,
  faLightbulb,
  faChartBar,
  faFilter,
  faSearch,
  faExternalLinkAlt
} from '@fortawesome/free-solid-svg-icons';
import apiService from '../services/api';
import LoadingScreen from './LoadingScreen';

const Logs = ({ initialFilter = 'all' }) => {
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [filter, setFilter] = useState(initialFilter);
  const [liveLogs, setLiveLogs] = useState([]);
  const [isStreaming, setIsStreaming] = useState(true);
  const [logCount, setLogCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [correlationMode, setCorrelationMode] = useState(false);
  const [selectedLogId, setSelectedLogId] = useState(null);

  // Update filter when initialFilter changes
  useEffect(() => {
    if (initialFilter && initialFilter !== 'all') {
      setFilter(initialFilter);
      setSearchTerm(initialFilter);
    }
  }, [initialFilter]);

  // Load initial EO/GIS logs when component mounts
  useEffect(() => {
    const initialLogs = [
      {
        id: 1001,
        timestamp: '2025-10-02 11:45:23',
        service: 'GDAL',
        level: 'ERROR',
        message: 'GDAL projection failure: CRS mismatch EPSG:32634 → EPSG:4326',
        filterType: 'gdal',
        datasetHandle: 'S2A_MSIL2A_20251002_T34SGA',
        crs: 'EPSG:32634',
        resolution: '10m',
        nodata: '-9999',
        filePath: 'pipelines/sentinel2/reproject.py',
        lineNumber: 147,
        codeSnippet: 'gdalwarp -t_srs EPSG:4326 -r bilinear input.tif output.tif\nERROR 1: Failed to reproject',
        aiSuggestion: 'CRS transformation failed. Check source CRS definition and ensure PROJ database is up to date.',
        rootCause: 'Source raster missing CRS metadata'
      },
      {
        id: 1002,
        timestamp: '2025-10-02 11:42:15',
        service: 'Rasterio',
        level: 'ERROR',
        message: 'RasterIO read error: corrupted GeoTIFF header',
        filterType: 'rasterio',
        datasetHandle: 'LC08_L2SP_192028_20251002_T1',
        crs: 'EPSG:32632',
        resolution: '30m',
        nodata: '0',
        filePath: 'pipelines/landsat/ingest.py',
        lineNumber: 89,
        codeSnippet: 'with rasterio.open("input.tif") as src:\n    data = src.read()\nRasterioIOError: Invalid TIFF header',
        aiSuggestion: 'GeoTIFF header corrupted. Re-download scene or use gdal_translate to repair.',
        rootCause: 'Incomplete file download or disk corruption'
      },
      {
        id: 1003,
        timestamp: '2025-10-02 11:38:47',
        service: 'Xarray/Dask',
        level: 'ERROR',
        message: 'NetCDF validation failed: incomplete HDF5 dimensions',
        filterType: 'netcdf',
        datasetHandle: 'MOD13Q1.A2025270.h18v04',
        crs: 'EPSG:4326',
        resolution: '250m',
        nodata: '-3000',
        filePath: 'pipelines/modis/validate_nc.py',
        lineNumber: 234,
        codeSnippet: 'ds = xr.open_dataset("modis.nc")\nValueError: dimension mismatch in HDF5 file',
        aiSuggestion: 'NetCDF file has missing or corrupted dimensions. Check file integrity with ncdump.',
        rootCause: 'Incomplete NetCDF write or HDF5 corruption'
      },
      {
        id: 1004,
        timestamp: '2025-10-02 11:35:12',
        service: 'GDAL',
        level: 'ERROR',
        message: 'GDAL projection failure: invalid PROJ string',
        filterType: 'gdal',
        datasetHandle: 'S2B_MSIL2A_20251002_T35SMC',
        crs: 'EPSG:32635',
        resolution: '10m',
        nodata: '-9999',
        filePath: 'lib/gis/transforms.py',
        lineNumber: 412,
        codeSnippet: 'gdal.Warp(output, input, dstSRS="EPSG:9999")\nERROR: Unknown CRS code',
        aiSuggestion: 'Invalid EPSG code. Use standard codes or provide full WKT definition.',
        rootCause: 'Non-existent CRS code in transformation'
      },
      {
        id: 1005,
        timestamp: '2025-10-02 11:30:05',
        service: 'GeoServer',
        level: 'WARNING',
        message: 'CRS mismatch in mosaic: EPSG:32632 vs EPSG:32634',
        filterType: 'crs',
        datasetHandle: 'Sentinel2_Mosaic_Mediterranean',
        crs: 'EPSG:32632',
        resolution: '10m',
        nodata: '-9999',
        filePath: 'services/geoserver/mosaic_builder.py',
        lineNumber: 56,
        codeSnippet: 'gdalwarp --config GDAL_CACHEMAX 512 -multi mosaic.vrt output.tif\nWARNING: CRS mismatch detected',
        aiSuggestion: 'Mosaic contains scenes with different UTM zones. Reproject all to common CRS before mosaicking.',
        rootCause: 'Mixed UTM zones in mosaic tiles'
      },
      {
        id: 1006,
        timestamp: '2025-10-02 11:28:33',
        service: 'STAC Indexer',
        level: 'WARNING',
        message: 'CRS mismatch in mosaic: source rasters have incompatible projections',
        filterType: 'crs',
        datasetHandle: 'Landsat_ARD_Mosaic_Europe',
        crs: 'EPSG:3857',
        resolution: '30m',
        nodata: '0',
        filePath: 'pipelines/stac/indexer.py',
        lineNumber: 178,
        codeSnippet: 'gdalbuildvrt mosaic.vrt tile1.tif tile2.tif\nWARNING: CRS definitions differ',
        aiSuggestion: 'Harmonize all source rasters to same CRS before creating mosaic VRT.',
        rootCause: 'Source tiles from different regions with different CRS'
      },
      {
        id: 1007,
        timestamp: '2025-10-02 11:25:19',
        service: 'PostGIS',
        level: 'ERROR',
        message: 'Orphaned raster tile detected: missing parent scene metadata',
        filterType: 'orphaned',
        datasetHandle: 'S2A_TILE_20251002_34SGA_B04',
        crs: 'EPSG:32634',
        resolution: '10m',
        nodata: '-9999',
        filePath: 'db/migrations/cleanup_tiles.sql',
        lineNumber: 23,
        codeSnippet: 'SELECT * FROM raster_tiles WHERE parent_scene_id IS NULL;\n-- Found 1 orphaned tile',
        aiSuggestion: 'Tile exists without parent scene record. Clean up orphaned tiles or restore parent metadata.',
        rootCause: 'Parent scene deleted but tiles not cascaded'
      },
      {
        id: 1008,
        timestamp: '2025-10-02 11:22:41',
        service: 'Rasterio',
        level: 'INFO',
        message: 'COG conversion completed successfully',
        filterType: 'info',
        datasetHandle: 'PSScene_20251002_102345_0f4a',
        crs: 'EPSG:4326',
        resolution: '3m',
        nodata: '0',
        filePath: 'pipelines/planet/cog_converter.py',
        lineNumber: 67,
        codeSnippet: 'gdal_translate -of COG -co COMPRESS=DEFLATE input.tif output_cog.tif\nInput file successfully converted to COG',
        aiSuggestion: 'System operating normally. No action required.',
        rootCause: null
      },
      {
        id: 1009,
        timestamp: '2025-10-02 11:18:55',
        service: 'PostGIS',
        level: 'WARNING',
        message: 'PostGIS ST_Intersects: slow query detected (2.3s)',
        filterType: 'warning',
        datasetHandle: 'vector_parcels_mediterranean',
        crs: 'EPSG:4326',
        resolution: 'N/A',
        nodata: 'N/A',
        filePath: 'services/vector/query_handler.py',
        lineNumber: 312,
        codeSnippet: 'SELECT COUNT(*) FROM parcels WHERE ST_Intersects(geom, ST_MakeEnvelope(...))\nExecution time: 2347ms',
        aiSuggestion: 'Add spatial index: CREATE INDEX idx_parcels_geom ON parcels USING GIST(geom);',
        rootCause: 'Missing or outdated spatial index on geometry column'
      },
      {
        id: 1010,
        timestamp: '2025-10-02 11:15:27',
        service: 'GeoServer',
        level: 'INFO',
        message: 'Sentinel-2 L2A processing started for tile 34SGA',
        filterType: 'info',
        datasetHandle: 'S2A_MSIL2A_20251002_T34SGA',
        crs: 'EPSG:32634',
        resolution: '10m',
        nodata: '-9999',
        filePath: 'pipelines/sentinel2/processor.py',
        lineNumber: 201,
        codeSnippet: 'sen2cor --resolution 10 --output_dir /data/L2A S2A_MSIL1C_20251002_T34SGA\nProcessing started...',
        aiSuggestion: 'System operating normally. No action required.',
        rootCause: null
      },
      {
        id: 1011,
        timestamp: '2025-10-02 11:12:45',
        service: 'h5py',
        level: 'WARNING',
        message: 'HDF5 dataset missing: unable to locate dataset "LST_Day_1km"',
        filterType: 'hdf5',
        datasetHandle: 'MOD11A1.A2025275.h18v04.006',
        crs: 'EPSG:4326',
        resolution: '1km',
        nodata: '-32768',
        filePath: 'pipelines/modis/hdf5_reader.py',
        lineNumber: 45,
        codeSnippet: 'import h5py\nwith h5py.File("MOD11A1.hdf", "r") as f:\n    lst = f["LST_Day_1km"][:]\nKeyError: "LST_Day_1km"',
        aiSuggestion: 'HDF5 dataset key not found. Verify dataset name using h5dump or check MODIS product documentation.',
        rootCause: 'Dataset name mismatch or corrupted HDF5 file structure'
      },
      {
        id: 1012,
        timestamp: '2025-10-02 11:10:33',
        service: 'h5py',
        level: 'WARNING',
        message: 'HDF5 dataset missing: incomplete metadata group',
        filterType: 'hdf5',
        datasetHandle: 'MOD13Q1.A2025270.h18v04',
        crs: 'EPSG:4326',
        resolution: '250m',
        nodata: '-3000',
        filePath: 'pipelines/modis/metadata_parser.py',
        lineNumber: 98,
        codeSnippet: 'with h5py.File("modis.hdf5", "r") as f:\n    metadata = f["HDFEOS/GRIDS/MODIS_Grid_16DAY_250m_500m_VI/Data Fields"]\nKeyError: Group not found',
        aiSuggestion: 'HDF5 group structure incomplete. Re-download file or check file integrity with h5dump -H.',
        rootCause: 'Incomplete HDF5 file download or write operation interrupted'
      },
      {
        id: 1013,
        timestamp: '2025-10-02 11:07:18',
        service: 'NumPy',
        level: 'WARNING',
        message: 'NumPy array shape mismatch: expected (10980, 10980, 13), got (10980, 10980, 12)',
        filterType: 'numpy',
        datasetHandle: 'S2A_MSIL2A_20251002_T34SGA',
        crs: 'EPSG:32634',
        resolution: '10m',
        nodata: '-9999',
        filePath: 'lib/raster/band_stacker.py',
        lineNumber: 134,
        codeSnippet: 'import numpy as np\nbands = np.stack([b1, b2, b3, ..., b12], axis=-1)\nValueError: could not broadcast input array from shape (10980,10980,12) into shape (10980,10980,13)',
        aiSuggestion: 'Band count mismatch. Sentinel-2 L2A should have 13 bands. Check if band 10 (cirrus) is missing from source data.',
        rootCause: 'Missing spectral band in source raster (band 10 cirrus likely excluded)'
      },
      {
        id: 1014,
        timestamp: '2025-10-02 11:03:52',
        service: 'Xarray',
        level: 'WARNING',
        message: 'Xarray chunking error: invalid chunk size for dimension "time"',
        filterType: 'xarray',
        datasetHandle: 'ERA5_Temperature_2025_Europe',
        crs: 'EPSG:4326',
        resolution: '0.25°',
        nodata: 'nan',
        filePath: 'pipelines/climate/era5_loader.py',
        lineNumber: 76,
        codeSnippet: 'ds = xr.open_dataset("era5.nc", chunks={"time": 1000, "lat": 100, "lon": 100})\nValueError: chunk size 1000 exceeds dimension size 365 for dimension "time"',
        aiSuggestion: 'Chunk size larger than dimension. Reduce chunk size to match or divide dimension size evenly.',
        rootCause: 'Chunk configuration exceeds actual temporal dimension'
      },
      {
        id: 1015,
        timestamp: '2025-10-02 10:58:24',
        service: 'Pandas',
        level: 'WARNING',
        message: 'Pandas dataframe merge failure: no common columns found',
        filterType: 'pandas',
        datasetHandle: 'STAC_Metadata_Sentinel2_Landsat8',
        crs: 'N/A',
        resolution: 'N/A',
        nodata: 'N/A',
        filePath: 'pipelines/stac/metadata_merger.py',
        lineNumber: 189,
        codeSnippet: 'import pandas as pd\nmerged = pd.merge(s2_metadata, landsat_metadata, on="scene_id")\nMergeError: No common columns to perform merge on',
        aiSuggestion: 'Column name mismatch. Sentinel-2 uses "granule_id" while Landsat uses "scene_id". Rename columns before merge.',
        rootCause: 'Metadata schema inconsistency between Sentinel-2 and Landsat STAC items'
      }
    ];

    setLiveLogs(initialLogs);
    setLogCount(initialLogs.length);

    // Random loading time between 1-2.5 seconds
    const loadingTime = 1000 + Math.random() * 1500;
    setTimeout(() => {
      setLoading(false);
    }, loadingTime);
  }, []);

  // Simulate live log streaming
  useEffect(() => {
    if (!isStreaming) return;

    const streamInterval = setInterval(() => {
      if (Math.random() < 0.4) { // 40% chance of new log
        const services = ['Xarray/Dask', 'Rasterio', 'GeoServer', 'PostGIS', 'STAC Indexer'];
        const stacItems = ['S2A_MSIL2A_20250929T102031_T34SGA', 'LC08_L2SP_192028_20250929_T1', 'MOD13Q1.A2025270.h18v04', 'PSScene_20250929_102345_0f4a'];
        const crsValues = ['EPSG:32634', 'EPSG:4326', 'EPSG:3857', 'EPSG:32632'];
        const resolutions = ['10m', '30m', '250m', '3m'];
        const nodataValues = ['-9999', 'nan', '0', '-32768'];

        const service = services[Math.floor(Math.random() * services.length)];
        const messages = [
          { text: 'COG conversion completed', type: 'info' },
          { text: 'GDAL projection failure: CRS mismatch EPSG:32634 → EPSG:4326', type: 'gdal' },
          { text: 'RasterIO read error: corrupted GeoTIFF header', type: 'rasterio' },
          { text: 'NetCDF validation failed: incomplete HDF5 dimensions', type: 'netcdf' },
          { text: 'CRS mismatch in mosaic: EPSG:32632 vs EPSG:32634', type: 'crs' },
          { text: 'Orphaned raster tile detected: missing parent scene', type: 'orphaned' },
          { text: 'PostGIS ST_Intersects: slow query detected', type: 'warning' },
          { text: 'GeoServer WMS GetMap timeout', type: 'warning' },
          { text: 'Sentinel-2 L2A processing started', type: 'info' },
          { text: 'Cloud mask validation: 12% cloud cover', type: 'info' },
          { text: 'STAC item validation passed', type: 'info' },
          { text: 'HDF5 dataset missing: unable to locate dataset', type: 'hdf5' },
          { text: 'NumPy array shape mismatch in band stacking', type: 'numpy' },
          { text: 'Xarray chunking error: invalid chunk size', type: 'xarray' },
          { text: 'Pandas dataframe merge failure: column mismatch', type: 'pandas' }
        ];
        const selectedMessage = messages[Math.floor(Math.random() * messages.length)];
        const newLog = {
          id: Date.now(),
          timestamp: new Date().toISOString().slice(0, 19).replace('T', ' '),
          service: service,
          level: selectedMessage.type === 'info' ? 'INFO' : (selectedMessage.type === 'warning' ? 'WARNING' : 'ERROR'),
          message: selectedMessage.text,
          filterType: selectedMessage.type,
          datasetHandle: stacItems[Math.floor(Math.random() * stacItems.length)],
          crs: crsValues[Math.floor(Math.random() * crsValues.length)],
          resolution: resolutions[Math.floor(Math.random() * resolutions.length)],
          nodata: nodataValues[Math.floor(Math.random() * nodataValues.length)],
          codeSnippet: `// GDAL processing log
gdalwarp -t_srs EPSG:4326 -r bilinear -co COMPRESS=LZW input.tif output.tif`,
          aiSuggestion: service === 'Xarray/Dask' ? 'Memory pressure detected → Suggest chunk=(1024,1024,1), increase --memory-limit, enable spill' : 'System operating normally. No action required.',
          rootCause: service === 'GeoServer' ? 'WMS timeout: missing overviews/COG format' : null
        };

        setLiveLogs(prev => [newLog, ...prev.slice(0, 19)]); // Keep only 20 logs
        setLogCount(prev => prev + 1);
      }
    }, 4000);

    return () => clearInterval(streamInterval);
  }, [isStreaming]);

  const getServiceIcon = (service) => {
    const icons = {
      'GeoServer': faGlobe,
      'GDAL': faDatabase,
      'SAGA-GIS': faCogs,
      'PostGIS': faDatabase,
      'Python/Rasterio': faFileCode
    };
    return icons[service] || faFileAlt;
  };

  const filteredLogs = liveLogs.filter(log => {
    // Filter by level/severity OR by custom filter type (gdal, rasterio, etc.)
    const levelMatch = filter === 'all' ||
                       (log.level || log.severity)?.toUpperCase() === filter ||
                       log.filterType === filter;

    // Filter by search term
    const searchMatch = searchTerm === '' ||
      log.message.toLowerCase().includes(searchTerm.toLowerCase()) ||
      log.service?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      log.tool?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      log.filterType?.toLowerCase().includes(searchTerm.toLowerCase());

    return levelMatch && searchMatch;
  });

  const toggleRow = (logId) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(logId)) {
      newExpanded.delete(logId);
    } else {
      newExpanded.add(logId);
    }
    setExpandedRows(newExpanded);
  };

  // Show loading state
  if (loading) {
    return (
      <div className="logs-container">
        <LoadingScreen message="Loading service logs..." />
      </div>
    );
  }

  // Show error state
  if (error) {
    return (
      <div className="logs-container">
        <div className="logs-header">
          <div className="header-title">
            <h2>
              <FontAwesomeIcon icon={faFileAlt} style={{ marginRight: '0.5rem' }} />
              Service Logs
            </h2>
          </div>
        </div>
        <div className="error-container">
          <FontAwesomeIcon icon={faExclamationTriangle} className="error-icon" />
          <h3>Connection Error</h3>
          <p>{error}</p>
          <button className="retry-btn" onClick={() => window.location.reload()}>
            <FontAwesomeIcon icon={faRefresh} style={{ marginRight: '0.5rem' }} />
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="logs-container">
      <div className="logs-header">
        <div className="header-title">
          <h2>
            <FontAwesomeIcon icon={faFileAlt} style={{ marginRight: '0.5rem' }} />
            Service Logs
          </h2>
          <div className="streaming-indicator">
            <div className={`streaming-dot ${isStreaming ? 'active' : ''}`}></div>
            <span className="streaming-text">
              {isStreaming ? 'STREAMING' : 'PAUSED'} • {logCount} total logs
            </span>
          </div>
        </div>
        <div className="log-controls">
          <div className="search-controls">
            <div className="search-box">
              <FontAwesomeIcon icon={faSearch} className="search-icon" />
              <input
                type="text"
                placeholder="Search logs, services, messages..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="search-input"
              />
              {searchTerm && (
                <button onClick={() => setSearchTerm('')} className="clear-search">
                  <FontAwesomeIcon icon={faTimes} />
                </button>
              )}
            </div>
          </div>

          <select
            className="filter-select"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
          >
            <option value="all">All Levels ({liveLogs.length})</option>
            <option value="ERROR">Errors ({liveLogs.filter(l => (l.level || l.severity)?.toUpperCase() === 'ERROR').length})</option>
            <option value="WARNING">Warnings ({liveLogs.filter(l => (l.level || l.severity)?.toUpperCase() === 'WARNING').length})</option>
            <option value="INFO">Info ({liveLogs.filter(l => (l.level || l.severity)?.toUpperCase() === 'INFO').length})</option>
          </select>

          <button
            className={`control-btn ${correlationMode ? 'active' : ''}`}
            onClick={() => setCorrelationMode(!correlationMode)}
            title="Enable correlation analysis"
          >
            <FontAwesomeIcon icon={faLink} style={{ marginRight: '0.25rem' }} />
            Correlate
          </button>

          <button
            className={`streaming-toggle-btn ${isStreaming ? 'active' : ''}`}
            onClick={() => setIsStreaming(!isStreaming)}
          >
            <FontAwesomeIcon icon={isStreaming ? faTimes : faRefresh} style={{ marginRight: '0.25rem' }} />
            {isStreaming ? 'Pause' : 'Start'} Stream
          </button>
        </div>
      </div>

      <div className="data-table">
        <div className="table-header">
          <h3 className="table-title">Service Logs ({filteredLogs.length} entries)</h3>
          <span className="table-subtitle">Click rows to expand details and AI suggestions</span>
        </div>
        <div className="table-content">
          <table className="log-table">
            <thead>
              <tr>
                <th className="timestamp-col">Timestamp</th>
                <th className="tool-col">Service</th>
                <th className="severity-col">Level</th>
                <th className="message-col">Message</th>
              </tr>
            </thead>
            <tbody>
              {filteredLogs.map((log, index) => (
                <React.Fragment key={log.id}>
                  <tr
                    className={`expandable ${expandedRows.has(log.id) ? 'expanded' : ''} ${index === 0 && Date.now() - log.id < 3000 ? 'log-row new-entry' : ''} ${correlationMode && selectedLogId === log.id ? 'selected-for-correlation' : ''} ${correlationMode && selectedLogId && selectedLogId !== log.id && log.service === liveLogs.find(l => l.id === selectedLogId)?.service ? 'correlated-service' : ''}`}
                    onClick={() => {
                      if (correlationMode) {
                        setSelectedLogId(selectedLogId === log.id ? null : log.id);
                      } else {
                        toggleRow(log.id);
                      }
                    }}
                  >
                    <td className="timestamp-col">{log.timestamp}</td>
                    <td className="tool-col">
                      <FontAwesomeIcon icon={getServiceIcon(log.service || log.tool)} style={{ marginRight: '0.5rem' }} />
                      {log.service || log.tool}
                    </td>
                    <td className="severity-col">
                      <span className={`severity-badge ${(log.level || log.severity)?.toLowerCase() || 'info'}`}>
                        <FontAwesomeIcon
                          icon={(log.level || log.severity) === 'ERROR' || (log.level || log.severity) === 'error' ? faTimes :
                                (log.level || log.severity) === 'WARNING' || (log.level || log.severity) === 'warning' ? faExclamationTriangle :
                                (log.level || log.severity) === 'INFO' || (log.level || log.severity) === 'info' ? faInfoCircle : faCheck}
                          style={{ marginRight: '0.25rem' }}
                        />
                        {(log.level || log.severity)?.toUpperCase() || 'INFO'}
                      </span>
                    </td>
                    <td className="message-col">{log.message}</td>
                  </tr>
                  {expandedRows.has(log.id) && (
                    <tr className="expandable-row">
                      <td colSpan="4">
                        <div className="expandable-content">
                          <div className="message-section">
                            <h4>
                              <FontAwesomeIcon icon={faFileAlt} style={{ marginRight: '0.5rem' }} />
                              Dataset Context
                            </h4>
                            <div className="full-message">{log.message}</div>
                            {log.datasetHandle && (
                              <div style={{ marginTop: '0.5rem', padding: '0.5rem', backgroundColor: 'rgba(59, 130, 246, 0.1)', borderRadius: '4px' }}>
                                <strong>STAC Item:</strong> <code>{log.datasetHandle}</code><br />
                                {log.crs && <><strong>CRS:</strong> <code>{log.crs}</code> </>}
                                {log.resolution && <><strong>Resolution:</strong> <code>{log.resolution}</code> </>}
                                {log.nodata && <><strong>NoData:</strong> <code>{log.nodata}</code></>}
                              </div>
                            )}
                          </div>

                          {log.codeSnippet && (
                            <div className="code-section">
                              <h4>
                                <FontAwesomeIcon icon={faCode} style={{ marginRight: '0.5rem' }} />
                                Command Context
                              </h4>
                              {log.filePath && (
                                <div style={{
                                  padding: '0.5rem',
                                  marginBottom: '0.5rem',
                                  background: 'rgba(239, 68, 68, 0.1)',
                                  border: '1px solid rgba(239, 68, 68, 0.3)',
                                  borderRadius: '4px',
                                  fontFamily: 'monospace',
                                  fontSize: '0.85rem'
                                }}>
                                  <span style={{ color: '#ef4444', fontWeight: 600 }}>📁 {log.filePath}</span>
                                  {log.lineNumber && (
                                    <span style={{ color: '#f59e0b', marginLeft: '0.5rem' }}>
                                      :<strong>{log.lineNumber}</strong>
                                    </span>
                                  )}
                                </div>
                              )}
                              <pre className="code-snippet">{log.codeSnippet}</pre>
                            </div>
                          )}

                          <div className="ai-suggestion">
                            <div className="ai-suggestion-header">
                              <FontAwesomeIcon icon={faBrain} />
                              <strong>AI Analysis & Next Actions</strong>
                              <span className={`confidence-badge ${(log.level || log.severity) === 'ERROR' || (log.level || log.severity) === 'error' ? 'high' : 'medium'}`}>
                                {(log.level || log.severity) === 'ERROR' || (log.level || log.severity) === 'error' ? 'Critical' : 'Analyzed'}
                              </span>
                            </div>
                            <div className="ai-suggestion-content">
                              <div className="ai-section">
                                <FontAwesomeIcon icon={faLightbulb} style={{ marginRight: '0.5rem', color: '#f59e0b' }} />
                                <strong>Root Cause:</strong>
                                <p>{log.rootCause || log.aiSuggestion || 'System operating normally. No immediate action required.'}</p>
                              </div>

                              <div className="ai-section">
                                <FontAwesomeIcon icon={faChartBar} style={{ marginRight: '0.5rem' }} />
                                <strong>Pattern Recognition:</strong>
                                <p>
                                  {(log.level || log.severity) === 'ERROR' || (log.level || log.severity) === 'error'
                                    ? `Similar errors from ${log.service || log.tool} occurred 3 times in the last hour. This suggests a recurring configuration or connectivity issue.`
                                    : `This message type appears ${Math.floor(Math.random() * 15) + 5} times per hour on average. Activity level is within normal parameters.`
                                  }
                                </p>
                              </div>

                              {correlationMode && selectedLogId === log.id && (
                                <div className="ai-section correlation-analysis">
                                  <FontAwesomeIcon icon={faLink} style={{ marginRight: '0.5rem' }} />
                                  <strong>Correlation Analysis:</strong>
                                  <p>
                                    Found {liveLogs.filter(l => l.service === log.service && l.id !== log.id).length} related log entries from {log.service || log.tool}.
                                    {(log.level || log.severity) === 'ERROR' || (log.level || log.severity) === 'error'
                                      ? ' Error cascade detected - this issue may be affecting downstream services.'
                                      : ' System dependencies are operating normally.'}
                                  </p>
                                  <div className="correlation-actions">
                                    <button className="action-btn">View Service Timeline</button>
                                    <button className="action-btn">Check Dependencies</button>
                                  </div>
                                </div>
                              )}

                              <div className="ai-section">
                                <strong>One-Click Actions:</strong>
                                <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.5rem', flexWrap: 'wrap' }}>
                                  {log.datasetHandle && (
                                    <button className="action-btn" style={{ fontSize: '0.85rem', padding: '0.4rem 0.8rem' }}>
                                      <FontAwesomeIcon icon={faExternalLinkAlt} style={{ marginRight: '0.25rem' }} />
                                      Open STAC Item
                                    </button>
                                  )}
                                  {(log.level === 'ERROR' || log.severity === 'error') && (
                                    <>
                                      <button className="action-btn" style={{ fontSize: '0.85rem', padding: '0.4rem 0.8rem' }}>
                                        <FontAwesomeIcon icon={faRefresh} style={{ marginRight: '0.25rem' }} />
                                        Rerun with Safe Defaults
                                      </button>
                                      <button className="action-btn" style={{ fontSize: '0.85rem', padding: '0.4rem 0.8rem' }}>
                                        <FontAwesomeIcon icon={faCode} style={{ marginRight: '0.25rem' }} />
                                        Show Failing Command
                                      </button>
                                    </>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default Logs;